import type { ReactNode } from 'react';
import type { ClientSidebarDataQuery } from 'src/gql/graphql';
import { useEffect, useMemo } from 'react';
import NextLink from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { Dialog } from '@base-ui/react/dialog';
import Avatar from '@savvy-kit/components/avatar';
import { Badge } from '@savvy-kit/components/badge';
import { Collapsible, CollapsibleContent } from '@savvy-kit/components/collapsible';
import { Separator } from '@savvy-kit/components/separator';
import { Text } from '@savvy-kit/foundations/text';
import { media, useMediaQuery } from '@savvy-kit/hooks/use-media-query';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import { Banknote, Flower, Folder, House, Map, Settings, Signal, Users } from 'lucide-react';
import TrackedButton from 'src/components/tracked/tracked-button';
import { useActiveHouseholdId } from 'src/hooks/use-active-household-id';
import { useAsPath } from 'src/hooks/use-as-path';
import { isAdvisor } from 'src/utils/account';

import type { JWTContextUser } from '@savvy/deprecated-shared/api/user-api';
import { CLIENT_STAGE_FULLY_ONBOARDED } from '@savvy/constants/client-stages';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { isSavvyTestUserEmail } from '@savvy/deprecated-shared/utils/internal-savvy';
import { loggedInAsAdvisorID } from '@savvy/deprecated-shared/utils/savvy-auth';
import { XSavvyLogo } from '@savvy/deprecated-shared/x-components/x-savvy-logo';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import CheckCircleIcon from '../../icons/custom/check-circle';
import { HouseholdSwitcherSidebarContainer } from '../household-switcher';
import { SavvyCircleIcon } from '../savvy-circle-icon';
import { Scrollbar } from '../scrollbar';
import { GET_CLIENT_SIDEBAR_DATA } from './client-sidebar-query';
import DashboardSidebarLogOutButton from './dashboard-sidebar-log-out-button';
import { DashboardSidebarSection } from './dashboard-sidebar-section';
import { useOrganizationBranding } from './use-organization-branding';

type DashboardSidebarProps = {
  readonly onClose: () => void;
  readonly open: boolean;
};

type Item = {
  title: string;
  disabled?: boolean;
  children?: Item[];
  chip?: ReactNode;
  icon?: ReactNode;
  path?: string;
  activePaths?: readonly string[];
};

type Section = {
  title: string;
  items: Item[];
};

type SidebarPortfolio = ClientSidebarDataQuery['client']['household']['portfolios'][number];

const sortedActiveItems = (items: Item[]) => {
  const getSortValue = (item: Item) => {
    const excludedFromDisabledSort = ['portfolio', 'financial-plan'];
    const isExcluded = excludedFromDisabledSort.some((path) => item.path?.includes(path));
    return Number(item.disabled && !isExcluded) || -1;
  };
  return items.sort((a, b) => getSortValue(a) - getSortValue(b));
};

const getAdvisorSections = (): Section[] => [
  {
    title: 'General',
    items: [
      {
        title: 'Clients',
        path: '/advisor',
        icon: <House className="size-5" />,
      },
      {
        title: 'Account',
        path: '/dashboard/account',
        icon: <Settings className="size-5" />,
      },
    ],
  },
];

const getClientSections = ({
  numberOfUndoneTasks,
  disableSavvyInvestmentsTab,
  secondaryPortfolios,
  personalPortfolio,
  user,
  advisorGroupShortDisplayName,
  enableTaxTab,
  hiddenPortalSurfaces,
}: {
  numberOfUndoneTasks: number;
  disableSavvyInvestmentsTab: boolean;
  secondaryPortfolios?: SidebarPortfolio[] | null;
  personalPortfolio?: SidebarPortfolio | null;
  user: JWTContextUser;
  advisorGroupShortDisplayName?: string | null;
  enableTaxTab?: boolean | null;
  hiddenPortalSurfaces?: readonly string[];
}): Section[] => {
  const hidden = new Set(hiddenPortalSurfaces ?? []);
  const showPlanning = !hidden.has('planning');
  const showTax = enableTaxTab === true && !hidden.has('tax');
  const showDocuments = !hidden.has('documents');
  const netWorthItem = {
    title: 'Net worth',
    path: '/portfolio',
    icon: <Signal size={20} />,
  };
  const accountItem = {
    title: 'Account',
    path: '/dashboard/account',
    icon: <Settings className="size-5" />,
  };
  if (user?.role === 'NON_ADVISORY_USER') {
    return [
      {
        title: '',
        items: [netWorthItem, accountItem],
      },
    ];
  }
  const isUserFullyOnboarded =
    !isNil(user) &&
    (!user.clientStage || user.clientStage === CLIENT_STAGE_FULLY_ONBOARDED || isSavvyTestUserEmail(user.email));

  // Determine investment title based on advisor group and portfolio type
  const getInvestmentTitle = () => {
    if (!isNil(advisorGroupShortDisplayName) && advisorGroupShortDisplayName !== '') {
      return `${advisorGroupShortDisplayName} Investments`;
    }
    return secondaryPortfolios && secondaryPortfolios.length > 0 ? 'Personal investments' : 'Savvy investments';
  };

  const savvyManagedItem = {
    title: getInvestmentTitle(),
    disabled: !isUserFullyOnboarded || disableSavvyInvestmentsTab,

    path: `/savvy-portfolio/${personalPortfolio?.id}`,
    icon: isUserFullyOnboarded && !disableSavvyInvestmentsTab ? <SavvyCircleIcon /> : <SavvyCircleIcon disabled />,
  };

  let savvySecondaryPortfolioItems: {
    title: string;
    path: string;
    icon: React.ReactElement;
  }[] = [];

  if (secondaryPortfolios) {
    savvySecondaryPortfolioItems = secondaryPortfolios.map((portfolio) => ({
      title: portfolio.displayName || portfolio.name,

      path: `/savvy-portfolio/${portfolio.id}`,
      icon: <SavvyCircleIcon />,
    }));
  }

  const dividerItem = {
    title: 'divider',
    path: 'divider',
  };

  const planningItem = {
    title: 'Planning',
    disabled: false,
    path: '/financial-plan',
    icon: <Flower size={20} />,
  };

  const tasksItem = {
    title: 'Tasks',
    path: '/tasks',
    icon: <CheckCircleIcon />,
    chip: numberOfUndoneTasks > 0 ? <Badge type="circle-sm">{numberOfUndoneTasks}</Badge> : null,
  };
  const advisorItem = {
    title: 'Advisor',
    path: '/dashboard/advisor',
    icon: <Users size={20} />,
  };
  const libraryItem = {
    title: 'Library',
    path: '/files',
    activePaths: ['/files', '/dashboard/reports'],
    icon: <Folder size={20} />,
  };
  const taxItem = {
    title: 'Tax Preparation',
    path: '/dashboard/tax',
    icon: <Banknote className="w-4 h-4" />,
  };

  if (isUserFullyOnboarded) {
    return [
      {
        title: '',
        items: sortedActiveItems([
          netWorthItem,
          savvyManagedItem,
          ...savvySecondaryPortfolioItems,
          dividerItem,
          ...(showPlanning ? [planningItem] : []),
          tasksItem,
          advisorItem,
          ...(showTax ? [taxItem] : []),
          ...(showDocuments ? [libraryItem] : []),
          accountItem,
        ]),
      },
    ];
  }

  const progressItem = {
    title: 'Getting started',
    path: '/dashboard',
    icon: <Map />,
  };
  return [
    {
      title: '',
      items: sortedActiveItems([
        progressItem,
        netWorthItem,
        savvyManagedItem,
        ...(showPlanning ? [planningItem] : []),
        tasksItem,
        advisorItem,
        ...(showTax ? [taxItem] : []),
        ...(showDocuments ? [libraryItem] : []),
        accountItem,
      ]),
    },
  ];
};

const getSections = (
  advisorView: boolean,
  numberOfUndoneTasks: number,
  disableSavvyInvestmentsTab: boolean,
  secondaryPortfolios: SidebarPortfolio[] | undefined,
  personalPortfolio: SidebarPortfolio | undefined,
  user: JWTContextUser,
  advisorGroupShortDisplayName?: string | null,
  enableTaxTab?: boolean | null,
  hiddenPortalSurfaces?: readonly string[],
): Section[] => {
  if (advisorView) {
    return getAdvisorSections();
  }
  return getClientSections({
    numberOfUndoneTasks,
    disableSavvyInvestmentsTab,
    secondaryPortfolios,
    personalPortfolio,
    user,
    advisorGroupShortDisplayName,
    enableTaxTab,
    hiddenPortalSurfaces,
  });
};

const HIDDEN_PORTAL_SURFACES_UNTIL_READY = ['tax', 'planning', 'documents'] as const;

export function DashboardSidebar(props: DashboardSidebarProps) {
  const { onClose, open } = props;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const asPath = useAsPath();
  const lgUp = useMediaQuery(media.up('lg'));
  const auth = useAuth();
  const { user } = auth;
  const advisorView = isAdvisor(auth);

  const isAdvisorLoggedInAsClient = !!loggedInAsAdvisorID();

  const { branding: advisorGroupBranding } = useOrganizationBranding();

  const { activeHouseholdId, canQuery } = useActiveHouseholdId();

  const { data } = useQueryWithErrorLogging(GET_CLIENT_SIDEBAR_DATA, {
    skip: advisorView || !canQuery,
    variables: {
      id: user?.id,
      householdId: activeHouseholdId ?? undefined,
    },
  });

  const role = data?.client.user.role;

  function shouldShowClientPortalMessageChip(): boolean {
    const clientPortalMessage = data?.client.household.clientPortalMessage;

    if (data?.client.user.isPrimaryClient) {
      return !!clientPortalMessage && !clientPortalMessage.primaryClientHasRead;
    }
    return !!clientPortalMessage && !clientPortalMessage.coClientHasRead;
  }
  const numberOfTodoTasks = data?.client.numberOfTodoTasks || 0;

  const disableSavvyInvestmentsTab = !!data?.client.household.adviceOnly || !data?.client.household.orionHouseholdId;
  // Until sidebar data settles, omit optional surfaces so firm-hidden items do not flash in.
  const sidebarReady = !isNil(data?.client);
  const enableTaxTab = sidebarReady && Boolean(data.client.enableTaxTab);
  const hiddenPortalSurfaces = sidebarReady
    ? data.client.household.hiddenPortalSurfaces
    : HIDDEN_PORTAL_SURFACES_UNTIL_READY;
  const secondaryPortfolios = data?.client.household.portfolios.filter(
    (portfolio: { isPersonal: boolean }) => !portfolio.isPersonal,
  );

  const personalPortfolio = data?.client.household.portfolios.find(
    (portfolio: { isPersonal: boolean }) => portfolio.isPersonal,
  );

  const sections = useMemo(() => {
    return getSections(
      advisorView,
      numberOfTodoTasks,
      disableSavvyInvestmentsTab,
      secondaryPortfolios,
      personalPortfolio,
      user,
      advisorGroupBranding?.shortDisplayName,
      enableTaxTab,
      hiddenPortalSurfaces,
    );
  }, [
    advisorView,
    numberOfTodoTasks,
    disableSavvyInvestmentsTab,
    secondaryPortfolios,
    personalPortfolio,
    user,
    advisorGroupBranding?.shortDisplayName,
    enableTaxTab,
    hiddenPortalSurfaces,
  ]);

  const handlePathChange = () => {
    if (isNil(searchParams)) {
      return;
    }

    if (open) {
      onClose();
    }
  };

  useEffect(handlePathChange, [searchParams, pathname]);

  if (!user) {
    return null;
  }
  if (!data && !advisorView) {
    return null;
  }
  const content = (
    <Scrollbar className={cn('h-full [&_.simplebar-content]:h-full', isAdvisorLoggedInAsClient && 'mt-16')}>
      <div className="flex flex-col h-full">
        <div>
          <NextLink
            className="flex flex-col items-center justify-center p-6 cursor-pointer no-underline"
            href="/dashboard/account"
          >
            <Avatar showFallbackIcon className="mb-2 size-20" imageUrl={user.profilePicUrl} />

            <Text variant="h4" color="dark">
              {}
              {`${user.firstName} ${user.lastName}`}
            </Text>
            <Collapsible open={shouldShowClientPortalMessageChip()}>
              <CollapsibleContent>
                <Badge
                  variant="primary-muted"
                  className="mt-2 cursor-pointer"
                  render={<NextLink href="/dashboard/advisor" />}
                >
                  1 message
                </Badge>
              </CollapsibleContent>
            </Collapsible>
          </NextLink>
          {!advisorView && <HouseholdSwitcherSidebarContainer />}
        </div>

        <div className="grow pr-4">
          {sections.map((section) => (
            <DashboardSidebarSection key={section.title} path={asPath} className="mt-4" {...section} />
          ))}
          {!advisorView && role !== 'NON_ADVISORY_USER' && (
            <div className="flex justify-center my-4">
              <NextLink href="/dashboard/advisor" className="no-underline">
                <TrackedButton
                  elementName="client-sidebar-contact-advisor-button"
                  variant="secondary"
                  size="sm"
                  className="w-[200px]"
                >
                  Contact your team
                </TrackedButton>
              </NextLink>
            </div>
          )}
          {role === 'NON_ADVISORY_USER' && (
            <div className="flex justify-center my-4">
              <NextLink
                href="https://share.hsforms.com/1JA-jv6f5SSCqwLJET0be_Arizft"
                target="_blank"
                className="no-underline"
              >
                <TrackedButton
                  elementName="client-sidebar-meet-with-advisor-button"
                  variant="secondary"
                  size="sm"
                  className="w-[200px]"
                >
                  Meet with an advisor
                </TrackedButton>
              </NextLink>
            </div>
          )}
        </div>
        <div className="flex flex-col items-start gap-3 pl-4 pr-4 mb-6">
          <DashboardSidebarLogOutButton onLogOut={onClose} fullWidth />
          <div className="flex flex-col gap-5 px-3 w-full">
            <Separator />
            {/* Custom branding if available, otherwise Savvy logo */}
            {!advisorView && !isNil(advisorGroupBranding?.logoUrl) && advisorGroupBranding.logoUrl !== '' ? (
              <div className="flex flex-col gap-2 items-start">
                {/* eslint-disable-next-line next/no-img-element -- dynamic remote branding URL, unsuited to next/image */}
                <img
                  src={advisorGroupBranding.logoUrl}
                  alt={
                    !isNil(advisorGroupBranding.displayName) && advisorGroupBranding.displayName !== ''
                      ? advisorGroupBranding.displayName
                      : 'Advisor Group Logo'
                  }
                  className="w-auto h-auto max-h-[60px] max-w-[160px] object-contain"
                />
              </div>
            ) : (
              <div className="flex flex-col gap-2 items-start">
                <div className="w-[66px] text-secondary">
                  <XSavvyLogo fill="currentColor" />
                </div>
                {!advisorView &&
                  !isNil(advisorGroupBranding?.displayName) &&
                  advisorGroupBranding.displayName !== '' && (
                    <Text variant="caption" color="secondary">
                      {advisorGroupBranding.displayName}
                    </Text>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Scrollbar>
  );

  // w-64 is `sidebarWidth` (256px) in deprecated-shared/theme, which dashboard-layout still
  // reads for the content offset and dashboard-navbar mirrors as lg:left-64.
  if (lgUp) {
    return (
      <aside
        aria-label="Main navigation"
        className="fixed top-0 right-auto left-0 z-[1100] flex h-full w-64 flex-col overflow-y-auto border-r border-solid border-r-[color:var(--savvy-divider)] bg-[var(--savvy-background-contrast)] text-[var(--savvy-neutral-50)] outline-none"
      >
        {content}
      </aside>
    );
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-[1300] bg-black/50 duration-300 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0" />
        <Dialog.Popup
          aria-label="Main navigation"
          className="fixed inset-y-0 left-0 z-[1300] flex h-full w-64 flex-col overflow-y-auto bg-[var(--savvy-background-default)] text-[var(--savvy-neutral-0)] shadow-[0px_10px_15px_rgba(100,116,139,0.12)] outline-none duration-300 data-open:animate-in data-open:slide-in-from-left data-closed:animate-out data-closed:slide-out-to-left"
        >
          {content}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default DashboardSidebar;
